import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import LoadingDNA from '../components/LoadingDNA';
import { getCompanyEmployees, getTrips, isApiError } from '../services/dashboardService';
import {
  ExpenseEventRecord,
  ReimbursementSummary,
  getExpenseEventById,
  getTripReimbursementSummary,
  isExpenseApiError,
} from '../services/expenseService';

const formatINR = (amount: number): string => `₹${amount.toLocaleString('en-IN')}`;
const formatDate = (value?: string): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const severityStyles: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-slate-100 text-slate-700',
};

const ExpenseEventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin/');
  const isManagerPath = location.pathname.startsWith('/manager/');
  const backPath = isAdminPath
    ? '/admin/team-approvals'
    : isManagerPath
      ? '/manager/dashboard'
      : '/employee/dashboard';

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [event, setEvent] = useState<ExpenseEventRecord | null>(null);
  const [employeeNames, setEmployeeNames] = useState<Map<string, string>>(new Map());
  const [tripMeta, setTripMeta] = useState<{ destination: string; purpose: string } | null>(null);
  const [reimbursement, setReimbursement] = useState<ReimbursementSummary | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        if (!eventId) {
          setErrorMessage('Missing expense event id.');
          setIsLoading(false);
          return;
        }

        const [eventData, employees, trips] = await Promise.all([
          getExpenseEventById(eventId),
          getCompanyEmployees(),
          getTrips({ page: 1, perPage: 500 }),
        ]);
        setEvent(eventData);
        setEmployeeNames(new Map(employees.map((item) => [item.id, item.name || item.email])));

        const trip = trips.find((item) => item.id === eventData.trip_id);
        if (trip) {
          setTripMeta({
            destination: trip.destination || 'No destination',
            purpose: trip.purpose || 'No purpose',
          });
        } else {
          setTripMeta(null);
        }

        try {
          const reimbursementSummary = await getTripReimbursementSummary(eventData.trip_id);
          setReimbursement(reimbursementSummary);
        } catch (_) {
          setReimbursement(null);
        }
      } catch (error) {
        if (isExpenseApiError(error)) setErrorMessage(error.message);
        else if (isApiError(error)) setErrorMessage(error.message);
        else if (error instanceof Error) setErrorMessage(error.message);
        else setErrorMessage('Failed to load expense event details.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [eventId]);

  const approvedSplitTotal = useMemo(
    () =>
      (event?.splits || [])
        .filter((split) => ['APPROVED', 'AUTO_APPROVED'].includes(String(split.status || '').toUpperCase()))
        .reduce((sum, split) => sum + Number(split.split_amount || 0), 0),
    [event],
  );

  const body = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[32px] font-extrabold text-[#0b5f77]">Expense Event Details</h1>
          <p className="text-[15px] font-semibold text-[#658496]">
            Full breakdown of payment, split ownership, and approvals.
          </p>
        </div>
        <Link
          to={backPath}
          className="rounded-full border border-[#c6ddea] px-4 py-2 text-[13px] font-bold text-[#0b708b] hover:bg-[#edf7fb]"
        >
          Back
        </Link>
      </div>

      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-[14px] font-semibold text-red-700">{errorMessage}</p> : null}

      {!isLoading && event ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <p className="text-[12px] font-extrabold tracking-[0.1em] text-[#8ea8ba]">EVENT TOTAL</p>
              <p className="mt-1 text-[32px] font-extrabold text-[#0b708b]">{formatINR(Number(event.total_amount || 0))}</p>
            </article>
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <p className="text-[12px] font-extrabold tracking-[0.1em] text-[#8ea8ba]">APPROVED SPLITS</p>
              <p className="mt-1 text-[32px] font-extrabold text-[#0b708b]">{formatINR(approvedSplitTotal)}</p>
            </article>
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <p className="text-[12px] font-extrabold tracking-[0.1em] text-[#8ea8ba]">AGGREGATE STATUS</p>
              <p className="mt-1 text-[26px] font-extrabold text-[#0b5f77]">{String(event.aggregate_status || '-').replaceAll('_', ' ')}</p>
            </article>
          </div>

          <article className="rounded-[16px] border border-[#deebf2] bg-white px-6 py-5">
            <h2 className="text-[24px] font-extrabold text-[#0b5f77]">Trust Layer</h2>
            {event.trust_signals?.flagged ? (
              <div className="mt-3 space-y-3">
                <p className="text-[14px] font-bold text-[#31556a]">
                  Risk Score: {event.trust_signals.risk_score}/100
                </p>
                {event.trust_signals.signals.map((signal) => (
                  <div key={signal.code} className="rounded-xl border border-[#e2edf4] bg-[#f8fbfd] px-4 py-3">
                    <div className="flex items-center gap-2">
                      <p className="text-[14px] font-extrabold text-[#0f5f78]">{signal.title}</p>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-wide ${severityStyles[signal.severity] || severityStyles.low}`}>
                        {signal.severity}
                      </span>
                    </div>
                    <p className="mt-1 text-[13px] font-semibold text-[#426476]">{signal.explanation}</p>
                    {signal.evidence ? (
                      <pre className="mt-2 overflow-x-auto rounded-lg bg-white p-2 text-[11px] font-semibold text-[#4f6d7d]">
{JSON.stringify(signal.evidence, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-[14px] font-semibold text-[#4f6d7d]">No trust flags triggered for this event.</p>
            )}
          </article>

          <article className="rounded-[16px] border border-[#deebf2] bg-white px-6 py-5">
            <h2 className="text-[24px] font-extrabold text-[#0b5f77]">Event Metadata</h2>
            <div className="mt-3 grid gap-3 text-[14px] font-semibold text-[#35566a] md:grid-cols-2">
              <p><span className="font-extrabold text-[#0f5f78]">Event ID:</span> {event.id}</p>
              <p><span className="font-extrabold text-[#0f5f78]">Trip ID:</span> {event.trip_id}</p>
              <p><span className="font-extrabold text-[#0f5f78]">Destination:</span> {tripMeta?.destination || '-'}</p>
              <p><span className="font-extrabold text-[#0f5f78]">Purpose:</span> {tripMeta?.purpose || '-'}</p>
              <p><span className="font-extrabold text-[#0f5f78]">Category:</span> {event.category || '-'}</p>
              <p><span className="font-extrabold text-[#0f5f78]">Merchant:</span> {event.merchant || '-'}</p>
              <p><span className="font-extrabold text-[#0f5f78]">Created:</span> {formatDate(event.created_at)}</p>
              <p><span className="font-extrabold text-[#0f5f78]">Split Strategy:</span> {event.split_strategy}</p>
            </div>
          </article>

          <article className="rounded-[16px] border border-[#deebf2] bg-white px-6 py-5">
            <h2 className="text-[24px] font-extrabold text-[#0b5f77]">Payments</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="text-left text-[12px] font-extrabold uppercase tracking-[0.08em] text-[#9ab4c4]">
                    <th className="pb-2">Payment ID</th>
                    <th className="pb-2">Paid By</th>
                    <th className="pb-2">Amount</th>
                    <th className="pb-2">Method</th>
                    <th className="pb-2">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {(event.payments || []).map((payment) => (
                    <tr key={payment.id} className="border-t border-[#ecf4f8] text-[14px] font-semibold text-[#2f4f61]">
                      <td className="py-2.5 font-bold text-[#0f5f78]">{payment.id.slice(0, 8)}</td>
                      <td className="py-2.5">{employeeNames.get(payment.paid_by_employee_id) || payment.paid_by_employee_id}</td>
                      <td className="py-2.5 font-extrabold text-[#183344]">{formatINR(Number(payment.amount_paid || 0))}</td>
                      <td className="py-2.5">{payment.payment_method || '-'}</td>
                      <td className="py-2.5">{payment.external_payment_ref || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <article className="rounded-[16px] border border-[#deebf2] bg-white px-6 py-5">
            <h2 className="text-[24px] font-extrabold text-[#0b5f77]">Split Ownership</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[920px]">
                <thead>
                  <tr className="text-left text-[12px] font-extrabold uppercase tracking-[0.08em] text-[#9ab4c4]">
                    <th className="pb-2">Split ID</th>
                    <th className="pb-2">Employee</th>
                    <th className="pb-2">Department</th>
                    <th className="pb-2">Manager</th>
                    <th className="pb-2">Amount</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(event.splits || []).map((split) => (
                    <tr key={split.id} className="border-t border-[#ecf4f8] text-[14px] font-semibold text-[#2f4f61]">
                      <td className="py-2.5 font-bold text-[#0f5f78]">{split.id.slice(0, 8)}</td>
                      <td className="py-2.5">{employeeNames.get(split.beneficiary_employee_id) || split.beneficiary_employee_id}</td>
                      <td className="py-2.5">{split.department || '-'}</td>
                      <td className="py-2.5">{split.manager_id ? (employeeNames.get(split.manager_id) || split.manager_id) : '-'}</td>
                      <td className="py-2.5 font-extrabold text-[#183344]">{formatINR(Number(split.split_amount || 0))}</td>
                      <td className="py-2.5">{String(split.status || '-').replaceAll('_', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <article className="rounded-[16px] border border-[#deebf2] bg-white px-6 py-5">
            <h2 className="text-[24px] font-extrabold text-[#0b5f77]">Approval Trail</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[920px]">
                <thead>
                  <tr className="text-left text-[12px] font-extrabold uppercase tracking-[0.08em] text-[#9ab4c4]">
                    <th className="pb-2">Approval ID</th>
                    <th className="pb-2">Split</th>
                    <th className="pb-2">Approver</th>
                    <th className="pb-2">Type</th>
                    <th className="pb-2">Decision</th>
                    <th className="pb-2">SLA Due</th>
                    <th className="pb-2">Decided</th>
                  </tr>
                </thead>
                <tbody>
                  {(event.approvals || []).map((approval) => (
                    <tr key={approval.id} className="border-t border-[#ecf4f8] text-[14px] font-semibold text-[#2f4f61]">
                      <td className="py-2.5 font-bold text-[#0f5f78]">{approval.id.slice(0, 8)}</td>
                      <td className="py-2.5">{approval.expense_split_id.slice(0, 8)}</td>
                      <td className="py-2.5">{employeeNames.get(approval.approver_id) || approval.approver_id}</td>
                      <td className="py-2.5">{approval.approver_type}</td>
                      <td className="py-2.5">{approval.decision}</td>
                      <td className="py-2.5">{formatDate(approval.sla_due_at || undefined)}</td>
                      <td className="py-2.5">{formatDate(approval.decided_at || undefined)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          {reimbursement ? (
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-6 py-5">
              <h2 className="text-[24px] font-extrabold text-[#0b5f77]">Trip Reimbursement Snapshot</h2>
              <div className="mt-3 grid gap-3 text-[14px] font-semibold text-[#35566a] md:grid-cols-2">
                <p><span className="font-extrabold text-[#0f5f78]">Payable Total:</span> {formatINR(Number(reimbursement.payable_total || 0))}</p>
                <p><span className="font-extrabold text-[#0f5f78]">Risk Total:</span> {formatINR(Number(reimbursement.risk_total || 0))}</p>
                <p><span className="font-extrabold text-[#0f5f78]">Reimbursed Total:</span> {formatINR(Number(reimbursement.reimbursed_total || 0))}</p>
                <p><span className="font-extrabold text-[#0f5f78]">Pending Entries:</span> {reimbursement.pending_entries}</p>
              </div>
            </article>
          ) : null}
        </>
      ) : null}
    </div>
  );

  if (isAdminPath) {
    return (
      <AdminShell
        pageTitle="Expense Event"
        subtitle="Deep view of payment, split ownership, and approval history."
      >
        {body}
      </AdminShell>
    );
  }

  return (
    <div className="min-h-screen bg-[#eaf4f8] px-4 py-5 sm:px-8">
      <div className="mx-auto max-w-[1280px] rounded-[20px] border border-[#dbe8ef] bg-[#f7fcff] p-5 sm:p-7">
        {body}
      </div>
    </div>
  );
};

export default ExpenseEventDetailPage;
